// 音檔以靜態 import 取得 URL：Vite 對 `import('*.mp3')` 會額外產生一支「只 export 一個 URL 字串」
// 的 ~700 bytes JS chunk，本檔的 howler 本身已是靜態相依、plugin 也是進場即載入，拆這 14 支 chunk
// 省不到 bytes，卻讓純裝飾性的音效多出 14 個跨網域 module fetch 失敗點；且該失敗會以未捕捉的
// unhandledrejection 逸出到全站 fire-and-forget 的 play() 呼叫端，並誤觸 02.chunk-recovery.client.js
// 的清快取 + 反註冊 SW + 整頁重整（CLIENT-PWA-SSR-1A9）。改為靜態 import 後 URL 直接進 entry bundle，
// 音檔本身由 Howler 取得，取不到時走 onloaderror 靜默降級，不再污染 chunk 錯誤通道。
import dmsBgmSound from 'assets/sounds/background/dms_bgm.mp3'
import loadingSound from 'assets/sounds/background/loading.mp3'
import lobbySound from 'assets/sounds/background/lobby.mp3'
import signUpSound from 'assets/sounds/background/sign_up.mp3'
import clickSound from 'assets/sounds/effect/click.mp3'
import closeSound from 'assets/sounds/effect/close.mp3'
import favoriteClickSound from 'assets/sounds/effect/favorite_click.mp3'
import favoriteUnclickSound from 'assets/sounds/effect/favorite_unclick.mp3'
import jackpotSound from 'assets/sounds/effect/jackpot.mp3'
import lvUpSound from 'assets/sounds/effect/lv_up.mp3'
import mailSound from 'assets/sounds/effect/mail.mp3'
import monsterClickSound from 'assets/sounds/effect/monster_click.mp3'
import monsterUnclickSound from 'assets/sounds/effect/monster_unclick.mp3'
import systemMailSound from 'assets/sounds/effect/system_mail.mp3'
import { Howl } from 'howler'

// iOS 判斷：涵蓋 iPhone/舊版 iPad，以及升級到 iPadOS 後把 UA 偽裝成 MacIntel 的新版 iPad
const isIOS = () =>
  !!(
    typeof navigator !== 'undefined' &&
    (navigator.userAgent.match(/\(i[^;]+;( U;)? CPU.+Mac OS X/) ||
      /iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1))
  )

export default defineNuxtPlugin(() => {
  class SoundControl {
    constructor() {
      this.background = new BackgroundSound()
      this.sound = new Sound()
      console.info('[Sound] initialized.')
    }

    mute(state) {
      this.background.mute(state)
      this.sound.mute(state)
    }

    fade(a, b, c) {
      this.background.fade(a, b, c)
      this.sound.fade(a, b, c)
    }

    volume() {
      return this.background.volume()
    }

    // 使用者音量（0-1）：music 與 sound 聲道各自獨立
    setMusicVolume(v) {
      this.background.setVolume(v)
    }

    setSoundVolume(v) {
      this.sound.setVolume(v)
    }

    getMusicVolume() {
      return this.background.baseVolume
    }

    // 清理所有音效資源，釋放記憶體
    cleanup() {
      this.background.unloadAll()
      this.sound.unloadAll()
    }
  }

  const soundControl = new SoundControl()

  if (import.meta.client) {
    window.soundControl = soundControl

    // 解決 iOS (或現代瀏覽器) 必須由使用者互動才能播放語音的問題
    const unlockAudio = () => {
      // 解鎖時同樣檢查可見性，避免後台點擊觸發播放
      if (!soundControl.background.isMuted && !soundControl.background.playing() && !document.hidden) {
        soundControl.background.play()
      }
      document.removeEventListener('click', unlockAudio)
      document.removeEventListener('touchstart', unlockAudio)
    }

    // 必須使用 { once: true } 或手動 remove，確保只會綁定觸發一次
    document.addEventListener('click', unlockAudio, { once: true })
    document.addEventListener('touchstart', unlockAudio, { once: true })
  }

  return {
    provide: {
      SoundControl: soundControl,
    },
  }
})

// 基礎音效類別
class BaseSound {
  constructor() {
    this.sounds = new Map()
    this.isMuted = true
    // 使用者音量（0-1）；靜音另走 isMuted，不共用 0 音量表示
    this.baseVolume = 1
    this.maxCacheSize = 20 // 限制最大快取數量，避免記憶體累積
    this.accessOrder = [] // 追蹤存取順序，用於 LRU 淘汰
    this._initializeSoundConfigs()
  }

  // 由子類別實作
  _initializeSoundConfigs() {
    this.configs = {}
  }

  // 更新 LRU 存取順序
  _updateAccessOrder(key) {
    const existingIndex = this.accessOrder.indexOf(key)
    if (existingIndex > -1) {
      this.accessOrder.splice(existingIndex, 1)
    }
    this.accessOrder.push(key)
  }

  // 淘汰最久未使用的音效
  _evictLRU() {
    while (this.sounds.size >= this.maxCacheSize && this.accessOrder.length > 0) {
      const oldestKey = this.accessOrder.shift()
      const oldSound = this.sounds.get(oldestKey)
      if (oldSound) {
        oldSound.stop()
        oldSound.unload()
        this.sounds.delete(oldestKey)
        console.log(`[Sound] LRU evicted: ${oldestKey}`)
      }
    }
  }

  async load(key) {
    if (!this.configs[key]) {
      console.warn(`Sound "${key}" not found`)
      return null
    }

    // 更新存取順序
    this._updateAccessOrder(key)

    if (this.sounds.has(key)) {
      return this.sounds.get(key)
    }

    // 檢查快取大小，超過時淘汰最久未使用的
    this._evictLRU()

    const config = this.configs[key]

    // 註：本方法自此至 sounds.set() 之間已無 await（音檔 URL 改為靜態 import），故不再需要
    // 「載入期間 config 被 setSource 換掉」的競態檢查，也不會有併發 load 重複建立 Howl 的問題。
    const options = { ...config.options }
    const isLoop = !!options.loop
    if (isLoop) {
      // RangeError: Maximum call stack size exceeded (CLIENT-PWA-SSR-V3 / CLIENT-PWA-SSR-NQ)
      // 原因：在某些瀏覽器 (如 VivoBrowser) 下，howler 原生 loop 會同步觸發 play -> ended -> play 循環
      // 做法：停用原生 loop，改由 onend 手動觸發非同步 play
      options.loop = false
    }
    // iOS 的 HTML5 Audio volume 唯讀，音量控制無效；改走 Web Audio（html5:false）讓音量/fade 可控。
    // 代價是音檔整份下載解碼進記憶體（bundle 資源同源、CDN 音源已開 CORS）
    if (options.html5 && isIOS()) {
      options.html5 = false
    }

    const sound = new Howl({
      src: [config.src],
      preload: true,
      mute: this.isMuted,
      volume: this.baseVolume,
      ...options,
      // 音檔取不到（行動網路 / CDN 抖動）時 Howler 走此 callback，不會拋例外。音效屬非必要的輔助
      // 體驗，此處靜默降級為「無聲」，僅留 console 訊號供 triage，不上報以免產生等量的配額噪音。
      onloaderror: (soundId, error) => {
        console.warn(`[Sound] Failed to load "${key}":`, error)
      },
      onend: () => {
        if (isLoop) {
          // 使用 setTimeout 打破同步呼叫堆疊
          setTimeout(() => {
            // 檢查 sound 是否仍有效 (未被 unload) 且視窗處於焦點狀態
            if (sound && typeof sound.state === 'function' && sound.state() === 'loaded' && !document.hidden) {
              sound.play()
            }
          }, 100)
        }
      },
    })

    this.sounds.set(key, sound)
    return sound
  }

  mute(state) {
    if (typeof state === 'undefined') {
      return this.isMuted
    }
    this.isMuted = state
    for (const sound of this.sounds.values()) {
      sound.mute(state)
    }
    return this.isMuted
  }

  // 設定使用者音量（0-1）並即時套用到所有已載入音軌；未載入的由 load() 的 volume 選項帶入。
  // 漸入中調音量不需特殊處理：Howler 的 volume() 會自動停掉進行中的 fade
  setVolume(v) {
    this.baseVolume = v
    for (const sound of this.sounds.values()) {
      sound.volume(v)
    }
  }

  fade(from, to, duration) {
    for (const sound of this.sounds.values()) {
      sound.fade(from, to, duration)
    }
  }

  stop() {
    for (const sound of this.sounds.values()) {
      sound.stop()
    }
  }

  // 清理所有音效資源，釋放記憶體
  unloadAll() {
    for (const sound of this.sounds.values()) {
      sound.stop()
      sound.unload()
    }
    this.sounds.clear()
    this.accessOrder = [] // 同步清理 LRU 順序追蹤
  }
}

// 音效類別（短音效）
class Sound extends BaseSound {
  _initializeSoundConfigs() {
    this.configs = {
      click: { src: clickSound },
      close: { src: closeSound },
      favoriteAdd: { src: favoriteClickSound },
      favoriteRemove: { src: favoriteUnclickSound },
      mailDefault: { src: mailSound },
      mailSystem: { src: systemMailSound },
      levelUp: { src: lvUpSound },
      openDrawer: { src: monsterClickSound },
      closeDrawer: { src: monsterUnclickSound },
    }
  }

  async play(soundType) {
    // 如果已靜音，提前返回，不載入也不播放
    if (this.isMuted) return

    const sound = await this.load(soundType)
    if (!sound) return

    // 只有在視窗處於焦點狀態時才播放短音效
    if (!document.hidden) {
      sound.play()
    }
  }
}

// 背景音樂類別（循環播放）
class BackgroundSound extends BaseSound {
  constructor() {
    super()
    this.scene = ''
    // 進站/重整後首次播放需漸入（規格：音樂從小漸進變大）
    this._fadeInPending = true
  }

  // 首次播放漸入：掛在 load 產生的音軌上，任一場景先播先觸發、僅一次
  async load(key) {
    const sound = await super.load(key)
    if (sound && this._fadeInPending && !sound._fadeInHooked) {
      sound._fadeInHooked = true
      sound.once('play', () => {
        if (!this._fadeInPending) return
        this._fadeInPending = false
        sound.fade(0, this.baseVolume, 2500)
      })
    }
    return sound
  }

  _initializeSoundConfigs() {
    this.configs = {
      index: {
        src: signUpSound,
        options: { loop: true, html5: true },
      },
      lobby: {
        src: lobbySound,
        options: { loop: true, html5: true },
      },
      loading: {
        src: loadingSound,
        options: { loop: true, html5: true },
      },
      diamondShop: {
        src: dmsBgmSound,
        options: { loop: true, html5: true },
      },
      jackpot: {
        src: jackpotSound,
        options: { loop: false, html5: true },
      },
    }
  }

  // 遊戲路由清單：在這些路由下，背景音樂切換預設靜音（jackpot 等特殊事件除外）
  static GAME_ROUTES = ['/game', '/sportsbook', '/luckysport']

  // 特殊事件音效：即使在遊戲內也需要強制播放
  static EVENT_SCENES = ['jackpot']

  _isGameRoute() {
    return typeof window !== 'undefined' && BackgroundSound.GAME_ROUTES.some((path) => window.location.pathname.includes(path))
  }

  async play(scene) {
    if (!scene) {
      // 無參數：重新播放當前場景（用於 iOS unlock 解鎖）
      if (this.isMuted) return

      let sound = this.sounds.get(this.scene)
      if (!sound) {
        sound = await this.load(this.scene)
      }
      // 只有在視窗處於焦點狀態時才播放
      if (sound && !document.hidden) {
        sound.play()
      }
      return
    }

    if (this.scene === scene && this.playing()) return

    const isEventScene = BackgroundSound.EVENT_SCENES.includes(scene)

    // 停止舊場景
    const currentSound = this.sounds.get(this.scene)
    if (currentSound) {
      currentSound.stop()
    }

    // 更新場景記錄
    this.scene = scene

    if (this.isMuted) return

    // 載入新場景音檔（非同步，可能耗時）
    const sound = await this.load(scene)
    if (!sound) return

    // 競態保護：load 等待期間若 scene 已被別處切換，放棄本次操作
    if (this.scene !== scene) return

    // 最終把關：確認當下是否仍在遊戲路由
    // 原因：load() 等待期間 game.vue 可能已 mounted 並呼叫 fade(1,0)
    // 但因音檔尚未就緒，該 fade 會撲空。此處補救，確保進遊戲後不發聲
    if (this._isGameRoute() && !isEventScene) {
      sound.volume(0)
      return
    }

    sound.volume(this.baseVolume)

    // 只有在視窗處於焦點狀態時才播放背景音樂 (Fixes: HMR noisy start)
    if (!document.hidden) {
      sound.play()
    }
  }

  async setSource(scene, newSource) {
    if (!scene || !newSource || !newSource.src) {
      console.warn('Invalid parameters for setSource')
      return
    }

    // 更新配置
    this.configs[scene] = {
      src: newSource.src,
      options: {
        loop: true,
        html5: true,
        ...newSource.options,
      },
    }

    // 如果該場景的音效已經載入，則需要重新載入
    if (this.sounds.has(scene)) {
      const currentSound = this.sounds.get(scene)
      currentSound.stop()
      currentSound.unload()
      this.sounds.delete(scene)
    }

    // 如果當前正在播放這個場景的音樂，且未靜音，需要重新載入並播放
    if (this.scene === scene && !this.isMuted) {
      console.log(`[Sound] Reloading background music for scene: ${scene} with new source`)
      const oldSound = this.sounds.get(scene)
      if (oldSound) {
        oldSound.stop()
        oldSound.unload()
        this.sounds.delete(scene)
      }

      const sound = await this.load(scene)
      // 重新載入時同樣檢查焦點
      if (sound && !document.hidden) {
        sound.play()
      }
    }
  }

  // 覆寫 stop 方法，只停止當前場景
  stop() {
    const sound = this.sounds.get(this.scene)
    if (sound) {
      sound.stop()
    }
  }

  playing() {
    const sound = this.sounds.get(this.scene)
    return sound ? sound.playing() : false
  }

  pause() {
    const sound = this.sounds.get(this.scene)
    if (sound) {
      sound.pause()
    }
  }

  // 覆寫 fade 方法，只 fade 當前場景
  fade(from, to, duration) {
    const sound = this.sounds.get(this.scene)
    if (sound) {
      // iOS 特判僅限 HTML5 音軌（volume 唯讀）；iOS 已改走 Web Audio（_html5 為 false）則走正常 fade
      if (isIOS() && sound._html5) {
        if (to === 0) {
          // iOS 的 HTML5 Audio 改變 volume 是無效的，所以 fade 沒用，直接 pause() 避免繼續佔用播放
          if (sound.playing()) sound.pause()
        } else if (to > 0) {
          sound.volume(to)
          setTimeout(() => {
            if (!sound.playing() && !document.hidden) sound.play()
          }, 500)
        }
      } else {
        // 非 iOS 裝置
        if (document.hidden && to > 0) {
          // 關鍵修正：如果標籤頁在後台且嘗試調大音量 (fade in)
          // 為了避免 howler.js 的 fade 自動啟動聲音，我們強制靜音並暫停
          sound.volume(0)
          if (sound.playing()) sound.pause()
          return
        }

        sound.fade(from, to, duration)

        // 解決離開遊戲後背景音樂沒恢復的問題：如果目標音量 > 0，確保音樂正在播放且在前台
        if (to > 0 && !sound.playing() && !document.hidden) {
          sound.play()
        }
      }
    }
  }

  volume(val) {
    const sound = this.sounds.get(this.scene)
    if (sound) {
      if (typeof val === 'number') {
        sound.volume(val)
      }
      return sound.volume()
    }
    return 0
  }
}
